import { useEffect, useState } from "react";
import api from "../utils/axios";
import Swal from "sweetalert2";
import { AnimatePresence, motion } from "framer-motion";

type TabType = "UPI" | "bank" | "CRYPTO";
type StatusType = "pending" | "accepted" | "paid" | "rejected";

interface WithdrawalItem {
  transaction_id: number;
  uid: string;
  name: string;
  amount: number;
  status: string;
  transaction_date: string;
  utr_no: string | null;
  transaction_type: TabType;
  bank?: any;
  crypto?: any;
  is_gateway_withdrawal: boolean;
  withdrawal_fee?: number;
  phone?: string;
}

const BANK_CODE_MAP: Record<string, any> = {
  "Access Bank": {
    slug: "access_bank",
    otpay_code: "80000001",
    fzpay_code: "000014",
  },
  "First Bank of Nigeria": {
    slug: "first_bank_of_nigeria",
    otpay_code: "80000007",
    fzpay_code: "000016",
  },
  "First Bank Nigeria": {
    slug: "first_bank_of_nigeria",
    otpay_code: "80000007",
    fzpay_code: "000016",
  },
  "United Bank for Africa": {
    slug: "united_bank_for_africa",
    otpay_code: "80000022",
    fzpay_code: "000004",
  },
  "UBA (United Bank for Africa)": {
    slug: "united_bank_for_africa",
    otpay_code: "80000022",
    fzpay_code: "000004",
  },
  "Zenith Bank": {
    slug: "zenith_bank",
    otpay_code: "80000025",
    fzpay_code: "000015",
  },
  "Fidelity Bank": {
    slug: "fidelity_bank",
    otpay_code: "80000006",
    fzpay_code: "000007",
  },
  "Union Bank of Nigeria": {
    slug: "union_bank_of_nigeria",
    otpay_code: "80000021",
    fzpay_code: "000018",
  },
  "Union Bank Nigeria": {
    slug: "union_bank_of_nigeria",
    otpay_code: "80000021",
    fzpay_code: "000018",
  },
  "Sterling Bank": {
    slug: "sterling_bank",
    otpay_code: "80000019",
    fzpay_code: "000001",
  },
  "First City Monument Bank": {
    slug: "fcmb",
    otpay_code: "80000008",
    fzpay_code: "000003",
  },
  "First City Monument Bank (FCMB)": {
    slug: "fcmb",
    otpay_code: "80000008",
    fzpay_code: "000003",
  },
  "Ecobank Nigeria": {
    slug: "ecobank_nigeria",
    otpay_code: "80000004",
    fzpay_code: "000010",
  },
  "Polaris Bank": {
    slug: "polaris_bank",
    otpay_code: "80000016",
    fzpay_code: "000008",
  },
  "Wema Bank": {
    slug: "wema_bank",
    otpay_code: "80000024",
    fzpay_code: "000017",
  },
  "Unity Bank": {
    slug: "unity_bank",
    otpay_code: "80000023",
    fzpay_code: "000011",
  },
  "Stanbic IBTC Bank": {
    slug: "stanbic_ibtc_bank",
    otpay_code: "80000017",
    fzpay_code: "000012",
  },
  "Kuda Bank": {
    slug: "kuda_bank",
    otpay_code: "80000028",
    fzpay_code: "090267",
  },
  "VFD Microfinance Bank": {
    slug: "vfd_microfinance_bank",
    otpay_code: "80000044",
    fzpay_code: "090110",
  },
  "Rubies Microfinance Bank": {
    slug: "rubies_microfinance_bank",
    otpay_code: "80000034",
    fzpay_code: "090175",
  },
  "OPay": {
    slug: "opay",
    otpay_code: "80000030",
    fzpay_code: "100004",
  },
  "Opay": {
    slug: "opay",
    otpay_code: "80000030",
    fzpay_code: "100004",
  },
  "PalmPay": {
    slug: "palmpay",
    otpay_code: "80000033",
    fzpay_code: "100033",
  },
  "Palmpay": {
    slug: "palmpay",
    otpay_code: "80000033",
    fzpay_code: "100033",
  },
};

const ManualWithdrawal = () => {
  const [tab, setTab] = useState<TabType>("bank");
  const [status, setStatus] = useState<StatusType>("pending");
  const [data, setData] = useState<WithdrawalItem[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchPhone, setSearchPhone] = useState("");
  const [selectedItems, setSelectedItems] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const [bankModal, setBankModal] = useState<any>(null);
  const [cryptoModal, setCryptoModal] = useState<any>(null);

  const token = localStorage.getItem("token");

  useEffect(() => {
    fetchData();
  }, [tab, status, page, searchPhone]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const res = await api.get(`/getManualWithdrawalData`, {
        params: {
          page,
          limit: 10,
          transactionType: tab,
          status,
          searchPhone: searchPhone || undefined,
        },
        headers: { Authorization: token },
      });

      setData(res.data.data || []);
      setTotalPages(res.data.totalPages || 1);
    } catch (error) {
      console.error("Error fetching data:", error);
      Swal.fire("Error", "Failed to fetch withdrawals", "error");
    } finally {
      setIsLoading(false);
    }
  };

  // Export all data based on current filters
  const exportAllToCSV = async () => {
    setIsExporting(true);
    try {
      // Fetch all data without pagination
      const res = await api.get(`/getManualWithdrawalData`, {
        params: {
          page: 1,
          limit: 10000, // Get all records
          transactionType: tab,
          status,
          searchPhone: searchPhone || undefined,
        },
        headers: { Authorization: token },
      });

      const allData = res.data.data || [];

      if (allData.length === 0) {
        Swal.fire("No Data", "No withdrawals found to export", "warning");
        return;
      }

      // Define CSV headers
      const csvRows = [
        [
          "Transaction ID",
          "User Name",
          "Phone Number",
          "Amount",
          "Status",
          "Transaction Date",
          "UTR Number",
          "Bank Name",
          "Account Holder Name",
          "Account Number",
          "Gateway Withdrawal",
          "Withdrawal Fee",
          "Transaction Type"
        ]
      ];

      // Add data rows
      for (const w of allData) {
        csvRows.push([
          w.transaction_id.toString(),
          w.name || "N/A",
          w.phone || "N/A",
          w.amount.toString(),
          getStatusText(w.status),
          new Date(w.transaction_date).toLocaleString(),
          w.utr_no || "N/A",
          w.bank?.bank_name || "N/A",
          w.bank?.account_holder_name || "N/A",
          w.bank?.account_number || "N/A",
          w.is_gateway_withdrawal ? "Yes" : "No",
          (w.withdrawal_fee || 0).toString(),
          tab
        ]);
      }

      // Create and download CSV file
      const csvContent = csvRows.map(row => {
        // Handle commas and quotes in data
        return row.map(cell => {
          if (typeof cell === 'string' && (cell.includes(',') || cell.includes('"'))) {
            return `"${cell.replace(/"/g, '""')}"`;
          }
          return cell;
        }).join(',');
      }).join('\n');

      // Add BOM for UTF-8 encoding to handle special characters
      const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
      link.href = url;
      link.setAttribute("download", `withdrawals_${status}_${timestamp}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      Swal.fire(
        "Export Successful",
        `${allData.length} withdrawals exported successfully`,
        "success"
      );
    } catch (error) {
      console.error("Error exporting data:", error);
      Swal.fire("Export Failed", "Failed to export withdrawals", "error");
    } finally {
      setIsExporting(false);
    }
  };

  // Export current page data
  const exportCurrentPageToCSV = () => {
    if (data.length === 0) {
      Swal.fire("No Data", "No withdrawals to export on current page", "warning");
      return;
    }

    const csvRows = [
      [
        "Transaction ID",
        "User Name",
        "Phone Number",
        "Amount",
        "Status",
        "Transaction Date",
        "UTR Number",
        "Bank Name",
        "Account Holder Name",
        "Account Number",
        "Gateway Withdrawal",
        "Withdrawal Fee"
      ]
    ];

    for (const w of data) {
      csvRows.push([
        w.transaction_id.toString(),
        w.name || "N/A",
        w.phone || "N/A",
        w.amount.toString(),
        getStatusText(w.status),
        new Date(w.transaction_date).toLocaleString(),
        w.utr_no || "N/A",
        w.bank?.bank_name || "N/A",
        w.bank?.account_holder_name || "N/A",
        w.bank?.account_number || "N/A",
        w.is_gateway_withdrawal ? "Yes" : "No",
        (w.withdrawal_fee || 0).toString()
      ]);
    }

    const csvContent = csvRows.map(row => row.join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
    link.href = url;
    link.setAttribute("download", `withdrawals_page${page}_${timestamp}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    Swal.fire("Export Successful", `${data.length} withdrawals exported`, "success");
  };

  const exportToCSV = (withdrawals: WithdrawalItem[]) => {
    const csvRows = [];
    csvRows.push(["User Phone", "Bank Name", "Account Holder", "Account Number", "Amount"]);

    for (const w of withdrawals) {
      csvRows.push([
        w.phone || "N/A",
        w.bank?.bank_name || "N/A",
        w.bank?.account_holder_name || "N/A",
        w.bank?.account_number || "N/A",
        w.amount.toString(),
      ]);
    }

    const csvContent = csvRows.map(row => row.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `withdrawals_${new Date().toISOString()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const processBulkGatewayWithdrawals = async (withdrawals: WithdrawalItem[], method: string) => {
    let successCount = 0;
    let failCount = 0;

    for (const w of withdrawals) {
      try {
        let gatewayData: any = {};

        if (method === "otpay" || method === "fzpay") {
          const bankName = w.bank?.bank_name;
          const bank = BANK_CODE_MAP[bankName];

          if (!bank) {
            console.error(`Bank not supported: ${bankName}`);
            failCount++;
            continue;
          }

          gatewayData = {
            gateway: method,
            bank_code: method === "otpay" ? bank.otpay_code : bank.fzpay_code,
            bank_slug: bank.slug,
          };
        } else if (method === "wgepay") {
          gatewayData = {
            gateway: "wgepay",
          };
        }

        await api.post("/approveRejectManualWithdrawal", {
          transactionId: w.transaction_id,
          utr_no: w.utr_no,
          uid: w.uid,
          coin: tab === "CRYPTO" ? "usdt" : "inr",
          amount: w.amount,
          isApproved: true,
          ...gatewayData,
          withdrawal_fee: w.withdrawal_fee || 0,
        });
        successCount++;
      } catch (error) {
        console.error(`Failed to process withdrawal ${w.transaction_id}:`, error);
        failCount++;
      }
    }

    return { successCount, failCount };
  };

  const handleBulkApprove = async () => {
    if (selectedItems.length === 0) {
      Swal.fire("No Selection", "Please select withdrawals to approve", "warning");
      return;
    }

    const selectedWithdrawals = data.filter(w => selectedItems.includes(w.transaction_id));

    // Check if all selected withdrawals have the same method
    const hasGatewayWithdrawal = selectedWithdrawals.some(w => w.is_gateway_withdrawal);
    const hasManualWithdrawal = selectedWithdrawals.some(w => !w.is_gateway_withdrawal);

    if (hasGatewayWithdrawal && hasManualWithdrawal) {
      Swal.fire(
        "Mixed Methods",
        "Please select either gateway withdrawals or manual withdrawals separately",
        "warning"
      );
      return;
    }

    // Confirm bulk approval
    const { value: confirm } = await Swal.fire({
      title: "Bulk Approval",
      html: `Are you sure you want to approve ${selectedItems.length} withdrawals?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Approve",
      cancelButtonText: "Cancel",
    });

    if (!confirm) return;

    // Password check
    const { value: code } = await Swal.fire({
      title: "Enter Approval Code",
      input: "password",
      showCancelButton: true,
    });

    if (code !== "010203") {
      Swal.fire("Invalid Code", "", "error");
      return;
    }

    setIsLoading(true);

    try {
      if (hasGatewayWithdrawal) {
        // For gateway withdrawals, ask which gateway to use
        const { value: method } = await Swal.fire({
          title: "Select Gateway",
          html: `
            <div style="display:flex; gap:10px; justify-content:center; padding:20px;">
              <div class="method-card" data-value="otpay">OTPay</div>
              <div class="method-card" data-value="fzpay">FZPay</div>
              <div class="method-card" data-value="wgepay">WgePay</div>
            </div>
            <style>
              .method-card {
                border:1.5px solid #d1d5db;
                border-radius:10px;
                padding:10px 18px;
                cursor:pointer;
                font-weight:500;
                font-size:13px;
                transition:0.2s;
              }
              .method-card:hover {
                border-color:#6366f1;
                background:#eef2ff;
              }
            </style>
          `,
          showCancelButton: true,
          confirmButtonText: "Process",
          preConfirm: () => {
            const active = document.querySelector(".method-card.selected");
            return active?.getAttribute("data-value");
          },
          didOpen: () => {
            const cards = document.querySelectorAll(".method-card");
            cards.forEach((card) => {
              card.addEventListener("click", () => {
                cards.forEach((c) => c.classList.remove("selected"));
                card.classList.add("selected");
              });
            });
          },
        });

        if (!method) {
          setIsLoading(false);
          return;
        }

        const { successCount, failCount } = await processBulkGatewayWithdrawals(
          selectedWithdrawals,
          method
        );

        Swal.fire(
          "Bulk Processing Complete",
          `✅ Success: ${successCount}\n❌ Failed: ${failCount}`,
          successCount > 0 ? "success" : "error"
        );
      } else {
        // Manual withdrawals - approve instantly and export CSV
        for (const w of selectedWithdrawals) {
          await api.post("/approveRejectManualWithdrawal", {
            transactionId: w.transaction_id,
            utr_no: w.utr_no,
            uid: w.uid,
            coin: tab === "CRYPTO" ? "usdt" : "inr",
            amount: w.amount,
            isApproved: true,
            withdrawal_fee: w.withdrawal_fee || 0,
          });
        }

        // Export CSV
        exportToCSV(selectedWithdrawals);

        Swal.fire(
          "Bulk Approval Complete",
          `${selectedItems.length} withdrawals approved and CSV exported`,
          "success"
        );
      }

      setSelectedItems([]);
      fetchData();
    } catch (error) {
      console.error("Bulk approval error:", error);
      Swal.fire("Error", "Failed to process bulk approval", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleAction = async (w: WithdrawalItem, isApproved: boolean) => {
    if (!isApproved) {
      const { value: code } = await Swal.fire({
        title: "Enter Approval Code",
        input: "password",
        showCancelButton: true,
      });

      if (code !== "010203") {
        Swal.fire("Invalid Code", "", "error");
        return;
      }

      await api.post("/approveRejectManualWithdrawal", {
        transactionId: w.transaction_id,
        utr_no: w.utr_no,
        uid: w.uid,
        coin: tab === "CRYPTO" ? "usdt" : "inr",
        amount: w.amount,
        isApproved: false,
      });

      fetchData();
      return;
    }

    const { value: method } = await Swal.fire({
      title: "Approve Withdrawal",
      html: `
        <div style="text-align:left; font-family:Inter, sans-serif">
          <div style="background:#f9fafb; border:1px solid #e5e7eb; border-radius:12px; padding:16px; margin-bottom:14px;">
            <div style="font-size:13px; color:#6b7280;">User</div>
            <div style="font-weight:600; font-size:16px;">${w.name}</div>
          </div>
          <div style="background:#ecfdf5; border:1px solid #10b981; border-radius:12px; padding:16px; margin-bottom:14px; text-align:center;">
            <div style="font-size:12px; color:#065f46;">Withdrawal Amount</div>
            <div style="font-size:22px; font-weight:700; color:#065f46;">${w.amount}</div>
          </div>
          <div style="border:1px solid #e5e7eb; border-radius:12px; padding:16px; margin-bottom:18px;">
            <div style="font-size:13px; font-weight:600; margin-bottom:10px;">🏦 Bank Details</div>
            <div style="display:grid; grid-template-columns:1fr 1fr; gap:10px; font-size:13px;">
              <div><div style="color:#6b7280;">Bank</div><div style="font-weight:500;">${w.bank?.bank_name}</div></div>
              <div><div style="color:#6b7280;">Account Name</div><div style="font-weight:500;">${w.bank?.account_holder_name}</div></div>
              <div><div style="color:#6b7280;">Account Number</div><div style="font-weight:500; letter-spacing:1px;">${w.bank?.account_number}</div></div>
              <div><div style="color:#6b7280;">Transaction ID</div><div style="font-weight:500;">${w.transaction_id}</div></div>
            </div>
          </div>
          <div style="display:flex; gap:10px; justify-content:center;">
            <div class="method-card" data-value="manual">Manual</div>
            <div class="method-card" data-value="wgepay">WgePay</div>
          </div>
        </div>
        <style>
          .method-card {
            border:1.5px solid #d1d5db;
            border-radius:10px;
            padding:10px 18px;
            cursor:pointer;
            font-weight:500;
            font-size:13px;
            transition:0.2s;
          }
          .method-card.active {
            border-color:#6366f1;
            background:#eef2ff;
            color:#4338ca;
          }
          .method-card:hover {
            border-color:#6366f1;
          }
        </style>
      `,
      showCancelButton: true,
      confirmButtonText: "Continue",
      didOpen: () => {
        const cards = document.querySelectorAll(".method-card");
        cards.forEach((card) => {
          card.addEventListener("click", () => {
            cards.forEach((c) => c.classList.remove("active"));
            card.classList.add("active");
            (Swal as any).selectedMethod = card.getAttribute("data-value");
          });
        });
        (Swal as any).selectedMethod = "otpay";
      },
      preConfirm: () => {
        return (Swal as any).selectedMethod;
      },
    });

    if (!method) return;

    const { value: code } = await Swal.fire({
      title: "Enter Approval Code",
      input: "password",
      showCancelButton: true,
    });

    if (code !== "010203") {
      Swal.fire("Invalid Code", "", "error");
      return;
    }

    let gatewayData: any = {};

    if (method === "otpay" || method === "fzpay") {
      const bankName = w.bank?.bank_name;
      const bank = BANK_CODE_MAP[bankName];

      if (!bank) {
        Swal.fire("Bank not supported!", "", "error");
        return;
      }

      gatewayData = {
        gateway: method,
        bank_code: method === "otpay" ? bank.otpay_code : bank.fzpay_code,
        bank_slug: bank.slug,
      };
    } else if (method === "wgepay") {
      gatewayData = {
        gateway: "wgepay",
      };
    }

    await api.post("/approveRejectManualWithdrawal", {
      transactionId: w.transaction_id,
      utr_no: w.utr_no,
      uid: w.uid,
      coin: tab === "CRYPTO" ? "usdt" : "inr",
      amount: w.amount,
      isApproved: true,
      ...gatewayData,
      withdrawal_fee: w.withdrawal_fee || 0,
    });

    fetchData();
  };

  const handleSelectAll = () => {
    if (selectedItems.length === data.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(data.map(w => w.transaction_id));
    }
  };

  const handleSelectItem = (id: number) => {
    if (selectedItems.includes(id)) {
      setSelectedItems(selectedItems.filter(i => i !== id));
    } else {
      setSelectedItems([...selectedItems, id]);
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case "pending":
        return "bg-yellow-100 text-yellow-700";
      case "accepted":
        return "bg-blue-100 text-blue-700";
      case "paid":
        return "bg-green-100 text-green-700";
      case "rejected":
        return "bg-red-100 text-red-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case "pending":
        return "Pending";
      case "accepted":
        return "Approved";
      case "paid":
        return "Paid";
      case "rejected":
        return "Rejected";
      default:
        return status;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-800">Withdrawal Management</h1>
        <p className="text-sm text-gray-500">Approve & manage withdrawals</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2">
        {["bank"].map((t) => (
          <button
            key={t}
            onClick={() => {
              setTab(t as TabType);
              setPage(1);
              setSelectedItems([]);
            }}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${tab === t ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-700"
              }`}
          >
            {t} Withdrawals
          </button>
        ))}
      </div>

      {/* Status Filter */}
      <div className="flex gap-2 flex-wrap">
        {["pending", "accepted", "paid", "rejected"].map((s) => (
          <button
            key={s}
            onClick={() => {
              setStatus(s as StatusType);
              setPage(1);
              setSelectedItems([]);
            }}
            className={`px-4 py-2 rounded-lg text-sm font-medium capitalize ${status === s
                ? "bg-gray-900 text-white"
                : "bg-gray-100 text-gray-700"
              }`}
          >
            {getStatusText(s)}
          </button>
        ))}
      </div>

      {/* Search & Bulk Actions */}
      <div className="flex justify-between items-center gap-4 flex-wrap">
        <div className="flex gap-2 flex-1 max-w-md">
          <input
            type="text"
            placeholder="Search by phone number..."
            value={searchPhone}
            onChange={(e) => setSearchPhone(e.target.value)}
            className="flex-1 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900"
          />
          <button
            onClick={() => {
              setPage(1);
              fetchData();
            }}
            className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800"
          >
            Search
          </button>
        </div>

        <div className="flex gap-2">
          {/* Export Dropdown */}
          <div className="relative group">
            <button
              disabled={isExporting}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              {isExporting ? (
                <>
                  <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Exporting...
                </>
              ) : (
                <>
                  📥 Export CSV
                  <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </>
              )}
            </button>
            <div className="absolute right-0 mt-0 w-48 bg-white rounded-md shadow-lg hidden group-hover:block z-10">
              <div className="py-1">
                <button
                  onClick={exportCurrentPageToCSV}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  Export Current Page ({data.length} records)
                </button>
                <button
                  onClick={exportAllToCSV}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  Export All ({status} withdrawals)
                </button>
              </div>
            </div>
          </div>

          {selectedItems.length > 0 && (
            <button
              onClick={handleBulkApprove}
              disabled={isLoading}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              Bulk Approve ({selectedItems.length})
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border rounded-2xl shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              <th className="px-4 py-3">
                <input
                  type="checkbox"
                  checked={selectedItems.length === data.length && data.length > 0}
                  onChange={handleSelectAll}
                  className="rounded"
                />
              </th>
              <th className="px-4 py-3">#</th>
              <th className="px-4 py-3">User</th>
              <th className="px-4 py-3">Phone</th>
              <th className="px-4 py-3">Amount</th>
              <th className="px-4 py-3">Details</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Date</th>
              <th className="px-4 py-3">Action</th>
            </tr>
          </thead>

          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={9} className="py-10 text-center text-gray-500">
                  Loading...
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td colSpan={9} className="py-10 text-center text-gray-500">
                  No records found
                </td>
              </tr>
            ) : (
              data.map((w, i) => (
                <tr key={w.transaction_id} className="border-t hover:bg-gray-50">
                  <td className="px-4 py-3">
                    {w.status === "pending" && (
                      <input
                        type="checkbox"
                        checked={selectedItems.includes(w.transaction_id)}
                        onChange={() => handleSelectItem(w.transaction_id)}
                        className="rounded"
                      />
                    )}
                  </td>
                  <td className="px-4 py-3">{i + 1 + (page - 1) * 10}</td>
                  <td className="px-4 py-3 font-medium">{w.name}</td>
                  <td className="px-4 py-3">{w.phone || "N/A"}</td>
                  <td className="px-4 py-3 font-semibold">
                    {tab === "CRYPTO" ? "$" : "৳"}
                    {w.amount.toFixed(2)}
                  </td>
                  <td className="px-4 py-3">
                    {tab === "UPI" && w.utr_no}
                    {tab === "bank" && (
                      <button
                        className="text-blue-600 underline"
                        onClick={() => setBankModal(w.bank)}
                      >
                        View Bank
                      </button>
                    )}
                    {tab === "CRYPTO" && (
                      <button
                        className="text-blue-600 underline"
                        onClick={() => setCryptoModal(w.crypto)}
                      >
                        View Wallet
                      </button>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`px-3 py-1 rounded-full text-xs ${getStatusBadgeClass(
                        w.status
                      )}`}
                    >
                      {getStatusText(w.status)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {new Date(w.transaction_date).toLocaleString()}
                  </td>
                  <td className="px-4 py-3">
                    {w.status === "pending" ? (
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleAction(w, true)}
                          className="px-3 py-1 bg-green-600 text-white rounded text-xs"
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => handleAction(w, false)}
                          className="px-3 py-1 bg-red-600 text-white rounded text-xs"
                        >
                          Reject
                        </button>
                      </div>
                    ) : (
                      "—"
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Pagination */}
        <div className="flex justify-between px-4 py-3 border-t">
          <span className="text-sm text-gray-500">
            Page {page} of {totalPages}
          </span>
          <div className="flex gap-2">
            <button
              disabled={page <= 1 || isLoading}
              onClick={() => setPage(page - 1)}
              className="px-4 py-1 border rounded disabled:opacity-40"
            >
              Previous
            </button>
            <button
              disabled={page >= totalPages || isLoading}
              onClick={() => setPage(page + 1)}
              className="px-4 py-1 border rounded disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Modals */}
      <AnimatePresence>
        {bankModal && (
          <Modal title="Bank Details" onClose={() => setBankModal(null)}>
            <pre className="text-xs overflow-auto">
              {JSON.stringify(bankModal, null, 2)}
            </pre>
          </Modal>
        )}

        {cryptoModal && (
          <Modal title="Crypto Wallet" onClose={() => setCryptoModal(null)}>
            <pre className="text-xs overflow-auto">
              {JSON.stringify(cryptoModal, null, 2)}
            </pre>
          </Modal>
        )}
      </AnimatePresence>
    </div>
  );
};

const Modal = ({ title, children, onClose }: any) => (
  <motion.div
    className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
  >
    <motion.div
      className="bg-white p-6 rounded-xl w-1/2 max-h-[80vh] overflow-auto"
      initial={{ scale: 0.8 }}
      animate={{ scale: 1 }}
    >
      <h3 className="text-lg font-semibold mb-4">{title}</h3>
      {children}
      <div className="text-right mt-4">
        <button
          onClick={onClose}
          className="px-4 py-2 bg-gray-800 text-white rounded"
        >
          Close
        </button>
      </div>
    </motion.div>
  </motion.div>
);

export default ManualWithdrawal;